import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

// ─── Referral banner ─────────────────────────────────────────────────────────
// Shows "{referrer} invited you" when the visitor arrived via a referral link.

function ReferralBanner({ referrerName }: { referrerName: string }) {
  return (
    <div className="mb-8 animate-fade-in">
      <div
        className="flex items-center gap-3 bg-gold bg-opacity-10 border border-gold
                   border-opacity-30 rounded-xl px-5 py-3.5 max-w-md mx-auto"
      >
        <span className="text-xl flex-shrink-0">🎁</span>
        <p className="font-body text-sm text-navy leading-snug">
          <strong>{referrerName}</strong> invited you.{' '}
          Start your free trial and they'll earn a credit when you subscribe.
        </p>
      </div>
    </div>
  );
}

// ─── Main CTA section ─────────────────────────────────────────────────────────
// The page's primary "Start free trial" anchor — LandingNav scrolls visitors
// here. Replaces the pre-launch waitlist form with a CTA that funnels into
// /signup (preserving any ?ref= referral via sessionStorage).

export default function SignupSection() {
  const [searchParams] = useSearchParams();
  const refCode        = searchParams.get('ref');

  const [referrerName, setReferrerName] = useState<string | null>(null);

  // Look up the referrer's first name if ?ref= present (best-effort; silent on failure).
  useEffect(() => {
    if (!refCode) return;
    fetch(`/api/waitlist/referrer?code=${encodeURIComponent(refCode)}`)
      .then((r) => r.json())
      .then((data: { firstName: string | null }) => {
        if (data.firstName) setReferrerName(data.firstName);
      })
      .catch(() => { /* non-fatal */ });
  }, [refCode]);

  // Preserve referral code through the journey to /signup
  const signupHref = refCode ? `/signup?ref=${encodeURIComponent(refCode)}` : '/signup';

  return (
    <section id="signup" className="py-24 bg-cream">
      <div className="section-container">
        <div className="max-w-lg mx-auto">

          {/* Referral banner */}
          {referrerName && <ReferralBanner referrerName={referrerName} />}

          {/* Heading */}
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl sm:text-4xl text-navy mb-3">
              Start mailing real cards today
            </h2>
            <p className="font-body text-gray-500 text-base">
              Set it once. Never miss another birthday, anniversary, or moment that matters.
            </p>
          </div>

          {/* CTA card */}
          <div className="card p-8 shadow-warm-lg text-center">
            <Link
              to={signupHref}
              className="w-full btn-primary inline-flex items-center justify-center"
            >
              Start your 14-day free trial
            </Link>
            <p className="mt-4 font-body text-sm text-gray-500">
              Already have an account?{' '}
              <Link to="/login" className="text-sage font-medium hover:underline">
                Log in
              </Link>
            </p>
          </div>

          {/* Trust signals */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            {[
              '✓ No credit card required',
              '✓ Cancel anytime',
              '✓ Real printed cards',
            ].map((t) => (
              <span key={t} className="font-body text-xs text-gray-400">{t}</span>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}
